import React, { useEffect, useState } from "react";
import { api, fmtMoney, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function Expenses() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: "", category: "Food", description: "", date: today() });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    const { data } = await api.get("/expenses", { params: { month } });
    setItems(data);
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const openAdd = () => {
    setEditing(null);
    setForm({ amount: "", category: "Food", description: "", date: today() });
    setOpenForm(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ amount: String(it.amount), category: it.category, description: it.description, date: it.date });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      amount: parseFloat(form.amount),
      category: form.category,
      description: form.description,
      date: form.date,
    };
    try {
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setOpenForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted");
    load();
  };

  const exportCsv = async () => {
    const url = `${API}/export/csv?month=${month}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `expenses_${month}.csv`;
    // Need credentials for cookie auth
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">LEDGER</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Expenses</h1>
          <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">{items.length} entries this month</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            data-testid="expense-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border-2 border-black px-3 py-2 font-mono text-sm"
          />
          <button
            data-testid="export-csv"
            onClick={exportCsv}
            className="border-2 border-black px-4 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors flex items-center gap-2"
          >
            <Download size={14} /> CSV
          </button>
          <button
            data-testid="add-expense-button"
            onClick={openAdd}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm hover:bg-[var(--action-blue)] transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> ADD EXPENSE
          </button>
        </div>
      </div>

      <div className="bg-white border border-black/15">
        <table className="w-full font-mono text-sm">
          <thead>
            <tr className="border-b-2 border-black">
              <th className="text-left p-3 overline">DATE</th>
              <th className="text-left p-3 overline">CATEGORY</th>
              <th className="text-left p-3 overline">DESCRIPTION</th>
              <th className="text-right p-3 overline">AMOUNT</th>
              <th className="text-right p-3 overline">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-[var(--text-secondary)]" data-testid="empty-expenses">No expenses yet — add your first one.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} data-testid={`expense-row-${it.id}`} className="border-b border-black/10 hover:bg-[var(--bg-page)]">
                <td className="p-3">{it.date}</td>
                <td className="p-3">
                  <span className="inline-block border border-black px-2 py-0.5 text-xs font-bold">{it.category.toUpperCase()}</span>
                </td>
                <td className="p-3 text-[var(--text-secondary)]">{it.description || "—"}</td>
                <td className="p-3 text-right font-bold">{fmtMoney(it.amount, currency)}</td>
                <td className="p-3 text-right">
                  <button data-testid={`edit-${it.id}`} onClick={() => openEdit(it)} className="p-2 hover:bg-black hover:text-white"><Pencil size={14} /></button>
                  <button data-testid={`delete-${it.id}`} onClick={() => del(it.id)} className="p-2 hover:bg-[var(--alert-red)] hover:text-white"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={openForm} onClose={() => setOpenForm(false)}>
        <form onSubmit={submit} data-testid="expense-form">
          <div className="overline mb-2">{editing ? "EDIT" : "ADD"} EXPENSE</div>
          <h3 className="font-display font-black text-2xl tracking-tighter mb-6">{editing ? "Update entry" : "New entry"}</h3>

          <label className="block mb-4">
            <div className="overline mb-1">AMOUNT</div>
            <input
              data-testid="form-amount"
              required type="number" step="0.01" min="0.01"
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
            />
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">CATEGORY</div>
            <select
              data-testid="form-category"
              value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border-2 border-black bg-white py-2 px-2 font-mono"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">DESCRIPTION</div>
            <input
              data-testid="form-description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
            />
          </label>
          <label className="block mb-6">
            <div className="overline mb-1">DATE</div>
            <input
              data-testid="form-date"
              required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full border-2 border-black bg-white py-2 px-2 font-mono"
            />
          </label>

          <div className="flex gap-2">
            <button type="button" onClick={() => setOpenForm(false)} className="flex-1 border-2 border-black py-2 font-display font-bold hover:bg-black hover:text-white">CANCEL</button>
            <button data-testid="form-submit" type="submit" className="flex-1 bg-black text-white py-2 font-display font-bold hover:bg-[var(--action-blue)]">{editing ? "SAVE" : "ADD"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
